import { __ } from '@wordpress/i18n';
import { startCase, isEmpty } from 'lodash';
import validator from 'validator';

/**
 * This function validates a form field based on the type of the field.
 *
 * @param {string}   type         - The type of the field. It can be 'email', 'text', 'integer', 'html' etc.
 * @param {string}   fieldName    - The name of the field.
 * @param {string}   value        - The value of the field to be validated.
 * @param {Object}   errors       - The existing errors object.
 * @param {Function} setErrors    - The function to update the errors object.
 * @param {Function} setShowToast - The function to control the visibility of the toast message.
 *
 * @return {boolean} - Returns true if the field is valid, otherwise it updates the errors object and returns false.
 */
const validateField = (
  type,
  fieldName,
  value,
  errors,
  setErrors,
  setShowToast,
) => {
  let error;
  switch (type) {
    case 'required':
      if (isEmpty(value)) {
        error = __('This field cannot be left blank.', 'b3rg');
      }
      break;
    case 'email':
      if (!validator.isEmail(value)) {
        error = __('Please enter a valid email address.', 'b3rg');
      }
      break;
    case 'text':
      if (!validator.isAlpha(value, 'en-US', { ignore: ' ' })) {
        error = __('Please enter only alphabetic characters.', 'b3rg');
      }
      break;
    case 'integer':
      if (!validator.isNumeric(value, { no_symbols: ' ' })) {
        error = __('Please enter only numeric values.', 'b3rg');
      }
      break;
    case 'html':
      if (!/<([A-Za-z][A-Za-z0-9]*)\b[^>]*>[\s\S]*?<\/\1>/.test(value)) {
        error = __('Please input valid HTML code.', 'b3rg');
      }
      break;
  }
  if (error) {
    setShowToast(true);
    setErrors({ ...errors, [fieldName]: error });
    return false;
  }
  const { [fieldName]: _, ...rest } = errors; // NOSONAR
  setErrors(rest);
  setShowToast(false);
  return true;
};

/**
 * Conditionally prevent Post from saving with a notification message
 *
 * @param {string} handle   Unique handle name for the error
 * @param {string} messages object of messages to be displayed
 * @param {string} type     Type of the notification
 * @return {void} Return nothing
 */
const preventPostSaveWithErrors = (handle, messages = {}, type = 'error') => {
  const lockPost = Object.keys(messages).length;
  if (lockPost) {
    // Convert messages object into a string with line breaks
    const message = Object.entries(messages)
      .map(
        ([key, value]) =>
          `${startCase(
            key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' '),
          )} : ${value}`,
      )
      .join('\n');
    wp.data.dispatch('core/editor').lockPostSaving(handle);
    wp.data
      .dispatch('core/notices')
      .createNotice(type, message, { id: handle, isDismissible: false });
  } else {
    wp.data.dispatch('core/editor').unlockPostSaving(handle);
    wp.data.dispatch('core/notices').removeNotice(handle);
  }
};

export { validateField, preventPostSaveWithErrors };
